import React, {createContext, PropsWithChildren, useContext, useState} from 'react';
import * as _ from 'lodash';
import {BankFileRow, InvoiceMatch, MatchedInvoicesRow} from '../types';
import {ExcelReader} from '../../_common/utils/excelReader';
import {matchInvoices} from '../_redux/bankStatementsActions';
import {useBankStatementsGeneralContext} from "./BankStatementsGeneralContext";

type BankStatementsContext = {
  tableRows: MatchedInvoicesRow[]
  loading: boolean
  handleMatchingInvoices: (file: File) => void
  handleClearRows: () => void
}

interface BankStatementsProviderProps extends PropsWithChildren<{}> {}

const BankStatementsContext = createContext<BankStatementsContext>({
  tableRows: [],
  loading: false,
  handleMatchingInvoices: () => {},
  handleClearRows: () => {},
});

export function useBankStatementsContext() {
  return useContext(BankStatementsContext);
}

export function BankStatementsProvider({children}: BankStatementsProviderProps) {
  const [loading, setLoading] = useState(false);
  const {tableRows, setTableRows} = useBankStatementsGeneralContext();

  const handleMatchingInvoices = async (file: File) => {
    try {
      setLoading(true);
      const content = await parseExcelFile(file);
      const matchedInvoices = await findMatchedInvoices(content);
      updateTableRows(content, matchedInvoices);
    } finally {
      setLoading(false);
    }
  };

  const parseExcelFile = async (file: File) => {
    const header = [
      'date',
      'affectiveDate',
      'description',
      'amount',
      'balance',
      'refID',
      'commission',
      'origin',
    ];
    const content = await ExcelReader.readFile<Omit<BankFileRow, 'rowID'>>(file, {header, range: 9});
    return content
      .filter(row => row.amount > 0)
      .map((row, index) => ({...row, rowID: index + 1}));
  };

  const findMatchedInvoices = async (content: BankFileRow[]) => {
    const potentialInvoicesData = content
      .map(row => _.pick(row, ['rowID', 'amount', 'description']));
    return matchInvoices(potentialInvoicesData);
  };

  const updateTableRows = (content: BankFileRow[], matchedInvoices: InvoiceMatch[]) => {
    const tableRows = content.map(row => {
      const matches = matchedInvoices.filter(invoice => invoice.rowID === row.rowID);
      return {
        id: row.rowID,
        amount: row.amount,
        description: row.description,
        createdAt: row.date,
        matchedCount: matches.length,
        matchedInvoices: matches,
      };
    });
    setTableRows(tableRows);
  };

  const handleClearRows = () => setTableRows([]);

  return (
    <BankStatementsContext.Provider value={{
      tableRows,
      loading,
      handleMatchingInvoices,
      handleClearRows
    }}>
      {children}
    </BankStatementsContext.Provider>
  );
}
